import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { TextField } from 'components/Field/TextField';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { PageHeader } from 'components/PageHeader';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { isEqual } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL_BACK } from 'store/actions';
import { REMOTE_ACCOUNT_SAVE_DETAILS } from 'store/remote/actions';

export const ProfileDetailsScreen: React.FC<
  ModalScreenProps<ModalScreens.SETTINGSPROFILEDETAILS>
> = ({ route, navigation }) => {
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();
  const forceUpdate = useForceUpdate();
  const remoteUser = route.params;

  const changes = React.useRef({
    ...remoteUser,
  });

  const checkIsDirty = () => !isEqual(remoteUser, changes.current);
  const isDirty = React.useRef<boolean>(false);
  isDirty.current = checkIsDirty();

  const shouldReload = () => {
    if (isDirty.current !== checkIsDirty()) {
      forceUpdate();
    }
  };

  const handleChangeWithName = (
    name: keyof Omit<typeof remoteUser, 'features'>,
    value: string,
  ) => {
    changes.current[name] = value;
    shouldReload();
  };

  const handleSave = React.useCallback(() => {
    if (!isEqual(remoteUser, changes.current)) {
      dispatch(REMOTE_ACCOUNT_SAVE_DETAILS(changes.current));
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [dispatch, remoteUser]);

  const handleClose = React.useCallback(() => {
    if (isDirty.current) {
      confirmUnsaved({
        onSave: () => handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [confirmUnsaved, handleSave, dispatch]);

  const isDirtyVar = isDirty.current;
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleClose);

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Details"
          onPressCancelButton={handleClose}
          onPressSaveButton={remoteUser.features.edit ? handleSave : undefined}
        />
      }
    >
      {!remoteUser.features.edit ? (
        <>
          <Card>
            <CardRow label="Name" value={changes.current.name} />
            <CardRow
              label="Phone"
              placeholder="No Value"
              value={changes.current.phone}
            />
          </Card>
          <Card>
            <CardRow
              label="Post-nominals"
              placeholder="No Value"
              value={changes.current.postNominals}
            />
            <CardRow
              label="Qualifications"
              placeholder="No Value"
              value={changes.current.qualifications}
            />
          </Card>
          <Card explanation="You cannot edit information on this page; contact your account's admin to make changes" />
        </>
      ) : (
        <>
          <Card label="Name">
            <TextField
              placeholder="First Name"
              value={changes.current.forename}
              onChangeWithName={handleChangeWithName}
              name="forename"
              autoCapitalize="words"
              autoCorrect={false}
              onEsc={handleClose}
              autoFocus
              autoFocusDelay
            />
            <TextField
              placeholder="Last Name"
              value={changes.current.surname}
              onChangeWithName={handleChangeWithName}
              name="surname"
              autoCapitalize="words"
              autoCorrect={false}
              onEsc={handleClose}
            />
          </Card>
          <Card label="Phone">
            <TextField
              placeholder="No Value"
              value={changes.current.phone}
              onChangeWithName={handleChangeWithName}
              name="phone"
              autoCapitalize="words"
              autoCorrect={false}
              onEsc={handleClose}
            />
          </Card>
          <Card label="Post-nominals">
            <TextField
              placeholder="No Value"
              value={changes.current.postNominals || ''}
              onChangeWithName={handleChangeWithName}
              name="postNominals"
              autoCapitalize="words"
              autoCorrect={false}
              onEsc={handleClose}
            />
          </Card>
          <Card label="Qualifications">
            <TextField
              placeholder="No Value"
              value={changes.current.qualifications}
              onChangeWithName={handleChangeWithName}
              name="qualifications"
              autoCapitalize="words"
              autoCorrect={false}
              onEsc={handleClose}
            />
          </Card>
        </>
      )}
    </Page>
  );
};
